import { MatterCollaborationEntrySource } from '@/analytics/enums/matter-collaboration.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'
import { getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'
import type { UserAccountData } from '@/firebase/auth/user-account-data'

const kMatterCollaborationEntrySources = new Set<string>(Object.values(MatterCollaborationEntrySource))

/**
 * Organization id for matter-sharing analytics events.
 *
 * Uses `orgData.id` only — the same source as `OrganizationInviteSent` — so org-level
 * collaboration dashboards join on one key. See analytics/README.md ("Matter collaboration").
 */
export function getCollaborationOrganizationId(userAccountData: UserAccountData | null | undefined): string {
  return userAccountData?.orgData?.id ?? ''
}

/**
 * Resolves the plain-string `dialog.source` (see `matter/store/slice.ts`) into a typed
 * `MatterCollaborationEntrySource`, mirroring `resolveMatterJurisdictionEntrySource`. Falls back to
 * `Unknown` for a missing or unrecognized value instead of guessing.
 */
export function resolveMatterCollaborationEntrySource(source: string | null): MatterCollaborationEntrySource {
  if (source != null && kMatterCollaborationEntrySources.has(source)) {
    return source as MatterCollaborationEntrySource
  }
  return MatterCollaborationEntrySource.Unknown
}

type MatterInviteTrackContext = {
  track: TrackEventFn | undefined
  matterId: string
  organizationId: string
  selectedRecipientCount: number
  matterInviteEntrySource: MatterCollaborationEntrySource
}

/**
 * Emits one `MatterInviteSent` per successfully invited recipient (not once per batch).
 * No-ops when `track` is unavailable or there are no invited recipients.
 * Swallows delivery errors per recipient so one failed emit cannot drop the rest of the batch
 * or alter invite/create product control-flow.
 */
export function trackMatterInviteSentForRecipients(
  params: MatterInviteTrackContext & {
    invitedRecipientUserIds: string[]
  }
): void {
  const { track, matterId, organizationId, selectedRecipientCount, matterInviteEntrySource, invitedRecipientUserIds } = params
  if (!track || invitedRecipientUserIds.length === 0) return

  const invitedCount = invitedRecipientUserIds.length
  for (const recipientUserId of invitedRecipientUserIds) {
    try {
      track(AnalyticsEvent.MatterInviteSent, {
        matterId,
        organizationId,
        recipientUserId,
        selectedRecipientCount,
        invitedCount,
        matterInviteEntrySource,
      })
    } catch {
      // Observational only — per-event delivery must not drop remaining recipients or affect product.
    }
  }
}

/**
 * Emits one `MatterInviteSendFailed` when the add-members request throws.
 * No-ops when `track` is unavailable.
 * Swallows delivery errors so analytics never alters product control-flow.
 */
export function trackMatterInviteSendFailed(params: MatterInviteTrackContext & { error: unknown }): void {
  const { track, matterId, organizationId, selectedRecipientCount, matterInviteEntrySource, error } = params
  if (!track) return

  try {
    track(AnalyticsEvent.MatterInviteSendFailed, {
      matterId,
      organizationId,
      selectedRecipientCount,
      matterInviteEntrySource,
      ...getAnalyticsApiErrorProps(error),
    })
  } catch {
    // Observational only — delivery must not affect invite/create success.
  }
}
